import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import DesignTokens from '../../styles/DesignTokens';
import { normalizeImageUrl } from '../../utils/urlUtils';

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  min-height: 200px;
  
  /* Ensure container doesn't break layout */
  display: block;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const CarouselImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NavigationButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
  flex-shrink: 0;

  ${CarouselContainer}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    width: 24px;
    height: 24px;
    color: #222;
    pointer-events: none;
    stroke-width: 2;
    flex-shrink: 0;
  }
`;

const PrevButton = styled(NavigationButton)`
  left: 12px;
`;

const NextButton = styled(NavigationButton)`
  right: 12px;
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 4px 8px;
  border-radius: ${DesignTokens.borderRadius.full};

  ${CarouselContainer}:hover & {
    opacity: 1;
  }
`;

const Dot = styled(motion.div)`
  width: 6px;
  height: 6px;
  border-radius: ${DesignTokens.borderRadius.full};
  background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
`;

function ImageCarousel({ images, mainImage, autoPlay = false, interval = 5000 }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [brokenImages, setBrokenImages] = useState(new Set());

  // Combine main image with additional images, ensuring no duplicates
  // Use Set to deduplicate URLs and normalize (decode) them
  const allImages = useMemo(() => {
    const imageSet = new Set();
    const result = [];
    
    // Normalize and add mainImage first if it exists
    if (mainImage && mainImage.trim()) {
      const normalized = normalizeImageUrl(mainImage);
      if (normalized && !imageSet.has(normalized)) {
        imageSet.add(normalized);
        result.push(normalized);
      }
    }
    
    // Add images array, normalizing and skipping duplicates
    if (images && Array.isArray(images)) {
      images.forEach(img => {
        if (img && img.trim()) {
          const normalized = normalizeImageUrl(img);
          if (normalized && !imageSet.has(normalized)) {
            imageSet.add(normalized);
            result.push(normalized);
          }
        }
      });
    }
    
    // If no images at all but we have mainImage, use it
    if (result.length === 0 && mainImage && mainImage.trim()) {
      const normalized = normalizeImageUrl(mainImage);
      if (normalized) {
        result.push(normalized);
      }
    }
    
    return result;
  }, [images, mainImage]);

  // Filter out broken images
  const validImages = allImages.filter(img => img && !brokenImages.has(img));

  // Handle image load errors
  const handleImageError = (imageUrl) => {
    setBrokenImages(prev => new Set([...prev, imageUrl]));
    // If current image is broken, move to next valid image
    if (validImages[currentIndex] === imageUrl && validImages.length > 1) {
      const nextIndex = (currentIndex + 1) % validImages.length;
      setCurrentIndex(nextIndex);
    }
  };

  // Auto-play functionality - must be before early returns
  useEffect(() => {
    if (!autoPlay || validImages.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, validImages.length]);

  // Update current index if it's out of bounds after filtering
  useEffect(() => {
    if (currentIndex >= validImages.length && validImages.length > 0) {
      setCurrentIndex(0);
    }
  }, [validImages.length, currentIndex]);

  if (validImages.length === 0) {
    return null;
  }

  if (validImages.length === 1) {
    return (
      <CarouselContainer>
        <CarouselImage 
          src={validImages[0]} 
          srcSet={`
            ${validImages[0]}?w=400 400w,
            ${validImages[0]}?w=800 800w,
            ${validImages[0]}?w=1200 1200w
          `}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          alt="Place"
          loading="lazy"
          onError={() => handleImageError(validImages[0])}
        />
      </CarouselContainer>
    );
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    if (index >= 0 && index < validImages.length) {
      setCurrentIndex(index);
    }
  };

  return (
    <CarouselContainer>
      <ImageWrapper>
        <AnimatePresence mode="wait">
          {validImages[currentIndex] && (
            <CarouselImage
              key={currentIndex}
              src={validImages[currentIndex]}
              alt={`Image ${currentIndex + 1}`}
              loading={currentIndex === 0 ? "eager" : "lazy"}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              onError={() => handleImageError(validImages[currentIndex])}
            />
          )}
        </AnimatePresence>
      </ImageWrapper>

      <PrevButton
        onClick={handlePrev}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ChevronLeftIcon />
      </PrevButton>

      <NextButton
        onClick={handleNext}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ChevronRightIcon />
      </NextButton>

      <DotsContainer>
        {validImages.map((_, index) => (
          <Dot
            key={index}
            active={index === currentIndex}
            onClick={() => goToSlide(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </DotsContainer>
    </CarouselContainer>
  );
}

export default ImageCarousel;

